<template>
  <v-container class="contact-page" fluid>
    <div class="head-wrap mb-4">
      <div>
        <h1 class="headline mb-1">Executive Management <span class="thai">คณะผู้บริหาร</span></h1>
        <p class="subhead mb-0">Manage board members and executive profiles shown on the corporate site.</p>
      </div>
      <v-btn v-if="canAdd" class="setup-btn" color="primary" @click="$router.push({name: 'executive-create'})">
        <v-icon left small>mdi-plus</v-icon>
        Create Profile
      </v-btn>
    </div>

    <v-card class="table-card">
      <div class="category-bar">
        <button v-for="item in typeFilters" :key="item.value" type="button" class="category-chip" :class="{'active': selectedType === item.value}" @click="onTypeChange(item.value)">
          {{ item.label }}
        </button>
      </div>
      <div class="filter-bar">
        <div class="search-wrap">
          <v-text-field v-model="searchText" append-icon="mdi-magnify" outlined dense hide-details label="Search by executive name..." clearable @input="onSearch" />
        </div>
      </div>

      <v-data-table :loading="isPageLoading" :headers="headers" :items="list" :items-per-page="10" hide-default-footer class="contact-table">
        <template v-slot:item.no="{index, item}">{{ rowNo(index, item) }}</template>
        <template v-slot:item.name="{item}">
          <div class="name-wrap">
            <strong>{{ item.name_en || item.name_th || '-' }}</strong>
            <div class="name-sub">{{ item.name_th || '-' }}</div>
          </div>
        </template>
        <template v-slot:item.position="{item}">{{ item.position_en || item.position_th || '-' }}</template>
        <template v-slot:item.type="{item}"><span class="type-pill" :class="item.executive_type">{{ typeLabel(item.executive_type) }}</span></template>
        <template v-slot:item.display_order="{item}">{{ item.display_order || 0 }}</template>
        <template v-slot:item.action="{item}">
          <button v-if="canEdit" class="icon-btn" @click.stop="goToEdit(item)"><v-icon size="16">mdi-pencil-outline</v-icon></button>
          <button v-if="canDelete" class="icon-btn ml-2" @click.stop="remove(item)"><v-icon size="16">mdi-delete-outline</v-icon></button>
        </template>
      </v-data-table>

      <div class="table-foot">Showing 1 to {{ list.length }} of {{ list.length }} entries</div>
    </v-card>

  </v-container>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import {hasPermision} from '@/helper/helper'

export default {
  data() {
    return {
      searchText: '',
      selectedType: '',
      localLoading: false,
      headers: [
        {text: 'NO.', value: 'no', sortable: false, width: 70},
        {text: 'EXECUTIVE NAME', value: 'name', sortable: false},
        {text: 'POSITION', value: 'position', sortable: false},
        {text: 'TYPE', value: 'type', sortable: false, width: 300},
        {text: 'DISPLAY ORDER', value: 'display_order', sortable: false, width: 180},
        {text: 'ACTIONS', value: 'action', sortable: false, width: 120, align: 'center'}
      ]
    }
  },
  computed: {
    ...mapState('executives', ['list', 'isLoading']),
    canAdd() { return hasPermision('CanAddExecutive') },
    canEdit() { return hasPermision('CanEditExecutive') },
    canDelete() { return hasPermision('CanDeleteExecutive') },
    isPageLoading() {
      return this.localLoading || this.isLoading
    },
    typeFilters() {
      return [
        { label: 'All', value: '' },
        { label: 'Board of Directors', value: 'board_of_directors' },
        { label: 'Executive Management', value: 'executive_management' }
      ]
    }
  },
  async created() {
    this.localLoading = true
    try {
      this.setResource('executives')
      this.setTextSearch('')
      this.setFilterType('')
      await this.fetch()
    } finally {
      this.localLoading = false
    }
  },
  methods: {
    ...mapActions('executives', ['setResource', 'fetch', 'setTextSearch', 'setFilterType', 'deleteById']),
    rowNo(index, item) {
      let rowIndex = Number(index)
      if (Number.isNaN(rowIndex)) {
        const fallbackIndex = (this.list || []).findIndex((listItem) => {
          if (item && item.id && listItem.id) return String(listItem.id) === String(item.id)
          return listItem === item
        })
        rowIndex = fallbackIndex >= 0 ? fallbackIndex : 0
      }
      return rowIndex + 1
    },
    typeLabel(type) {
      return type === 'board_of_directors' ? 'Board of Directors' : 'Executive Management'
    },
    async onTypeChange(type) {
      this.localLoading = true
      try {
        this.selectedType = type
        this.setFilterType(type)
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    async onSearch() {
      this.localLoading = true
      try {
        this.setTextSearch(this.searchText || '')
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    goToEdit(item) {
      this.$router.push({name: 'executive-edit', params: {id: item.id}})
    },
    async remove(item) {
      if (!window.confirm('Delete this record?')) return
      this.localLoading = true
      try {
        const ok = await this.deleteById(item.id)
        if (ok) await this.fetch()
      } finally {
        this.localLoading = false
      }
    }
  }
}
</script>

<style scoped>
.contact-page { background: #f7f9fc; min-height: calc(100vh - 120px); }
.head-wrap { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.headline { font-size: 30px !important; color: #0f1f3b; }
.thai { color: #64748b; font-size: 15px; font-weight: 500; margin-left: 6px; }
.subhead { color: #64748b; font-size: 13px; }
.setup-btn { text-transform: none; font-weight: 700; border-radius: 10px; }
.table-card { border: 1px solid #e6e9f0; border-radius: 10px; box-shadow: none; overflow: hidden; }
.category-bar { display: flex; gap: 10px; padding: 14px; border-bottom: 1px solid #edf2fb; flex-wrap: wrap; }
.category-chip { border: 1px solid #dfe7f6; background: #fff; color: #5f7397; border-radius: 999px; padding: 8px 18px; font-size: 14px; font-weight: 700; cursor: pointer; }
.category-chip.active { background: #eef4ff; border-color: #bcd3ff; color: #1e49ba; }
.filter-bar { display: flex; gap: 10px; padding: 14px; border-bottom: 1px solid #edf2fb; }
.search-wrap { flex: 1; }
.name-wrap { line-height: 1.25; }
.name-sub { color: #7284a5; font-size: 13px; }
.type-pill { border-radius: 8px; padding: 4px 12px; font-weight: 700; font-size: 14px; }
.type-pill.board_of_directors { background: #eef4ff; color: #1e49ba; }
.type-pill.executive_management { background: #f1efff; color: #6d57f5; }
.icon-btn { width: 30px; height: 30px; border-radius: 7px; border: 1px solid #e6e9f0; background: #fff; }
.table-foot { padding: 14px 16px; border-top: 1px solid #edf2fb; color: #64748b; font-size: 12px; }
.contact-table::v-deep .v-data-table-header th { color: #7c8aa5 !important; font-size: 12px; font-weight: 700; }
.contact-table::v-deep td { font-size: 14px; }
</style>
